import { createAction } from '@reduxjs/toolkit';
import type { IErrorType } from '../../constants/types';
import type { ICreatorGrantablePlan } from '../../types/global/creatorSubscription.types';
import type {
    AdminSubscriptionKind,
    IAdminCreatorSubscriptionListQuery,
    IAdminCreatorSubscriptionListResult,
    IAdminCreatorSubscriptionRow,
    ISwitchAdminCreatorPaystackPlanPayload,
} from '../../utils/adminCreatorSubscriptionDirectory';
import type {
    FlutterwaveCatalogItem,
    FlutterwaveSyncKind,
    FlutterwaveSyncResult,
    SyncFlutterwavePlansInput,
} from '../../utils/adminFlutterwavePlanSync';
import { types } from './types';

export const fetchAdminSubscriptionsByKind = createAction<{
    kind: AdminSubscriptionKind;
    params?: IAdminCreatorSubscriptionListQuery;
}>(types.FETCH_ADMIN_SUBSCRIPTIONS_BY_KIND);
export const fetchAdminSubscriptionsByKindSuccess = createAction<IAdminCreatorSubscriptionListResult>(
    types.FETCH_ADMIN_SUBSCRIPTIONS_BY_KIND_SUCCESS,
);
export const fetchAdminSubscriptionsByKindFail = createAction<IErrorType>(types.FETCH_ADMIN_SUBSCRIPTIONS_BY_KIND_FAIL);

export const expireAdminCreatorTrial = createAction<{
    subscriptionId: string;
    mode?: 'convert' | 'cancel';
}>(types.EXPIRE_ADMIN_CREATOR_TRIAL);
export const expireAdminCreatorTrialSuccess = createAction<{
    note?: string;
    subscription?: IAdminCreatorSubscriptionRow;
}>(types.EXPIRE_ADMIN_CREATOR_TRIAL_SUCCESS);
export const expireAdminCreatorTrialFail = createAction<IErrorType>(types.EXPIRE_ADMIN_CREATOR_TRIAL_FAIL);

export const cancelAdminCreatorSubscription = createAction<string>(types.CANCEL_ADMIN_CREATOR_SUBSCRIPTION);
export const cancelAdminCreatorSubscriptionSuccess = createAction<{
    note?: string;
    subscription?: IAdminCreatorSubscriptionRow;
}>(types.CANCEL_ADMIN_CREATOR_SUBSCRIPTION_SUCCESS);
export const cancelAdminCreatorSubscriptionFail = createAction<IErrorType>(
    types.CANCEL_ADMIN_CREATOR_SUBSCRIPTION_FAIL,
);

export const cancelAdminCampaignHostSubscription = createAction<{
    subscriptionId: string;
    cancelImmediately?: boolean;
}>(types.CANCEL_ADMIN_CAMPAIGN_HOST_SUBSCRIPTION);
export const cancelAdminCampaignHostSubscriptionSuccess = createAction<{
    note?: string;
    subscription?: IAdminCreatorSubscriptionRow;
}>(types.CANCEL_ADMIN_CAMPAIGN_HOST_SUBSCRIPTION_SUCCESS);
export const cancelAdminCampaignHostSubscriptionFail = createAction<IErrorType>(
    types.CANCEL_ADMIN_CAMPAIGN_HOST_SUBSCRIPTION_FAIL,
);

export const resetAdminCreatorTrial = createAction<string>(types.RESET_ADMIN_CREATOR_TRIAL);
export const resetAdminCreatorTrialSuccess = createAction(types.RESET_ADMIN_CREATOR_TRIAL_SUCCESS);
export const resetAdminCreatorTrialFail = createAction<IErrorType>(types.RESET_ADMIN_CREATOR_TRIAL_FAIL);

export const fetchAdminPaystackSwitchPlans = createAction(types.FETCH_ADMIN_PAYSTACK_SWITCH_PLANS);
export const fetchAdminPaystackSwitchPlansSuccess = createAction<ICreatorGrantablePlan[]>(
    types.FETCH_ADMIN_PAYSTACK_SWITCH_PLANS_SUCCESS,
);
export const fetchAdminPaystackSwitchPlansFail = createAction<IErrorType>(types.FETCH_ADMIN_PAYSTACK_SWITCH_PLANS_FAIL);

export const switchAdminCreatorPaystackPlan = createAction<{
    subscriptionId: string;
    payload: ISwitchAdminCreatorPaystackPlanPayload;
}>(types.SWITCH_ADMIN_CREATOR_PAYSTACK_PLAN);
export const switchAdminCreatorPaystackPlanSuccess = createAction<Record<string, unknown>>(
    types.SWITCH_ADMIN_CREATOR_PAYSTACK_PLAN_SUCCESS,
);
export const switchAdminCreatorPaystackPlanFail = createAction<IErrorType>(
    types.SWITCH_ADMIN_CREATOR_PAYSTACK_PLAN_FAIL,
);

export const chargeAdminCreatorPaystackAuthorization = createAction<{
    subscriptionId: string;
    force?: boolean;
}>(types.CHARGE_ADMIN_CREATOR_PAYSTACK_AUTHORIZATION);
export const chargeAdminCreatorPaystackAuthorizationSuccess = createAction<{
    note?: string;
    subscription?: IAdminCreatorSubscriptionRow;
    charge?: Record<string, unknown>;
}>(types.CHARGE_ADMIN_CREATOR_PAYSTACK_AUTHORIZATION_SUCCESS);
export const chargeAdminCreatorPaystackAuthorizationFail = createAction<IErrorType>(
    types.CHARGE_ADMIN_CREATOR_PAYSTACK_AUTHORIZATION_FAIL,
);

export const fetchFlutterwaveSyncCatalog = createAction<FlutterwaveSyncKind>(types.FETCH_FLUTTERWAVE_SYNC_CATALOG);
export const fetchFlutterwaveSyncCatalogSuccess = createAction<FlutterwaveCatalogItem[]>(
    types.FETCH_FLUTTERWAVE_SYNC_CATALOG_SUCCESS,
);
export const fetchFlutterwaveSyncCatalogFail = createAction<IErrorType>(types.FETCH_FLUTTERWAVE_SYNC_CATALOG_FAIL);

export const syncFlutterwavePlansAdmin = createAction<SyncFlutterwavePlansInput>(types.SYNC_FLUTTERWAVE_PLANS_ADMIN);
export const syncFlutterwavePlansAdminSuccess = createAction<FlutterwaveSyncResult>(
    types.SYNC_FLUTTERWAVE_PLANS_ADMIN_SUCCESS,
);
export const syncFlutterwavePlansAdminFail = createAction<IErrorType>(types.SYNC_FLUTTERWAVE_PLANS_ADMIN_FAIL);
